import {
  AuthLayout,
  Button,
  Checkbox,
  Field,
  Input,
  Label,
  PasswordInput,
  Shake,
  applyFieldErrors,
  errorMessage,
} from '@bixo/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, getRouteApi, useNavigate } from '@tanstack/react-router'
import { AlertCircle, ArrowLeft, ArrowRight, KeyRound, ShieldCheck, Smartphone } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { platformBadges, platformBrand, platformFeatures } from '../components/auth-brand'
import { PlatformShowcase } from '../components/auth-showcase'
import { endpoints, qk } from '../lib/api'
import type { Operator } from '../lib/types'

const schema = z.object({
  email: z.email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password'),
  remember: z.boolean(),
})

const route = getRouteApi('/login')

const slide = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
  transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] as const },
}

function FormError({ message }: { message: string | null }) {
  return (
    <AnimatePresence initial={false}>
      {message && (
        <motion.div
          role="alert"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="overflow-hidden"
        >
          <div className="flex items-start gap-2.5 rounded-lg border border-danger/25 bg-danger-soft px-3 py-2.5 text-sm text-danger">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            {message}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export function LoginPage() {
  const { redirect } = route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)
  const [failures, setFailures] = useState(0)
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', remember: true },
  })

  const [challenge, setChallenge] = useState(false)
  const [code, setCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)

  const fail = (message: string | null): void => {
    setFormError(message)
    setFailures((n) => n + 1)
  }

  const finish = async (user: Operator): Promise<void> => {
    queryClient.setQueryData(qk.me, user)
    await navigate({ to: redirect ?? '/' })
  }

  const verify = useMutation({
    mutationFn: () =>
      endpoints.twoFactorChallenge(
        useRecovery ? { recovery_code: code.trim() } : { code: code.replace(/\s/g, '') },
      ),
    onSuccess: ({ user }) => finish(user),
    onError: (error) => fail(errorMessage(error)),
  })

  const login = useMutation({
    mutationFn: endpoints.login,
    onSuccess: async (result) => {
      if (result.two_factor || !result.user) {
        setFormError(null)
        setChallenge(true)
        return
      }
      await finish(result.user)
    },
    onError: (error) => {
      if (applyFieldErrors(error, form.setError, ['email', 'password'] as const)) fail(null)
      else fail(errorMessage(error))
    },
  })

  return (
    <AuthLayout
      brand={platformBrand}
      title={challenge ? 'Two-step verification' : 'Welcome back'}
      subtitle={
        challenge
          ? useRecovery
            ? 'Enter one of the recovery codes you saved.'
            : 'Open your authenticator app and enter the 6-digit code.'
          : 'Sign in to the Bixo platform console.'
      }
      footer={
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck className="size-3.5" /> Restricted to the Bixo team. Every action is audited.
        </span>
      }
      showcase={
        <PlatformShowcase
          eyebrow="Platform console"
          title="Run every business on Bixo from one calm, powerful place."
        />
      }
      features={platformFeatures}
      badges={platformBadges}
    >
      <Shake trigger={failures}>
        <AnimatePresence mode="wait" initial={false}>
          {challenge ? (
            <motion.form
              key="challenge"
              {...slide}
              onSubmit={(e) => {
                e.preventDefault()
                setFormError(null)
                verify.mutate()
              }}
              className="grid gap-5"
              noValidate
            >
              <FormError message={formError} />
              <div className="flex items-center gap-3 rounded-xl border border-border bg-surface-2/60 p-3">
                <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-brand-600 text-white">
                  {useRecovery ? <KeyRound className="size-5" /> : <Smartphone className="size-5" />}
                </span>
                <p className="text-sm text-muted-foreground">
                  {useRecovery ? 'Each recovery code works once.' : 'Codes refresh every 30 seconds.'}
                </p>
              </div>
              <Field label={useRecovery ? 'Recovery code' : 'Authenticator code'}>
                {(c) => (
                  <Input
                    {...c}
                    value={code}
                    onChange={(e) =>
                      setCode(useRecovery ? e.target.value : e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    inputMode={useRecovery ? 'text' : 'numeric'}
                    autoComplete="one-time-code"
                    autoFocus
                    placeholder={useRecovery ? 'xxxx-xxxx' : '••••••'}
                    className="tabular h-14 text-center text-2xl tracking-[0.5em]"
                  />
                )}
              </Field>
              <Button type="submit" size="lg" className="w-full" loading={verify.isPending}>
                Verify and sign in <ArrowRight />
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-1 text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setChallenge(false)
                    setCode('')
                    setFormError(null)
                  }}
                >
                  <ArrowLeft className="size-3.5" /> Back
                </button>
                <button
                  type="button"
                  className="cursor-pointer font-medium text-brand-600 hover:underline dark:text-brand-400"
                  onClick={() => {
                    setUseRecovery((v) => !v)
                    setCode('')
                  }}
                >
                  {useRecovery ? 'Use the authenticator app' : 'Use a recovery code'}
                </button>
              </div>
            </motion.form>
          ) : (
            <motion.form
              key="password"
              {...slide}
              onSubmit={form.handleSubmit(
                (values) => {
                  setFormError(null)
                  login.mutate(values)
                },
                () => setFailures((n) => n + 1),
              )}
              className="grid gap-5"
              noValidate
            >
              <FormError message={formError} />
              <Field label="Email" error={form.formState.errors.email?.message}>
                {(c) => (
                  <Input
                    {...c}
                    type="email"
                    autoComplete="username"
                    autoFocus
                    placeholder="you@company.com"
                    className="h-11"
                    {...form.register('email')}
                  />
                )}
              </Field>
              <Field label="Password" error={form.formState.errors.password?.message}>
                {(c) => (
                  <PasswordInput
                    {...c}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    {...form.register('password')}
                  />
                )}
              </Field>
              <div className="-mt-1 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Controller
                    control={form.control}
                    name="remember"
                    render={({ field }) => (
                      <Checkbox
                        id="remember"
                        checked={field.value}
                        onCheckedChange={(v) => field.onChange(v === true)}
                      />
                    )}
                  />
                  <Label htmlFor="remember" className="font-normal text-muted-foreground">
                    Keep me signed in
                  </Label>
                </div>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
                >
                  Forgot password?
                </Link>
              </div>
              <Button type="submit" size="lg" className="group w-full" loading={login.isPending}>
                Sign in
                <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
              </Button>
              <div className="relative py-1 text-center text-xs text-muted-foreground">
                <span className="absolute inset-x-0 top-1/2 h-px bg-border" aria-hidden />
                <span className="relative bg-surface px-3">New to Bixo?</span>
              </div>
              <Button asChild variant="secondary" size="lg" className="w-full">
                <Link to="/signup">Start your free 14-day trial</Link>
              </Button>
            </motion.form>
          )}
        </AnimatePresence>
      </Shake>
    </AuthLayout>
  )
}
